import { getErrorMessage } from '../hooks/use-toast';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contentApi } from '../lib/api-client';

interface Content {
  id: string;
  type: string;
  title: string;
  body: string;
  status: string;
  publishedAt: string | null;
  scheduledFor: string | null;
  createdAt: string;
  categories: Array<{ category: Category }>;
}

interface Category {
  id: string;
  slug: string;
  name: string;
  contentType: string;
}

type ContentType = 'all' | 'announcement' | 'newsletter' | 'document' | 'event';
type ContentStatus = 'all' | 'draft' | 'scheduled' | 'published' | 'archived';

export function ContentPage() {
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState<ContentType>('all');
  const [statusFilter, setStatusFilter] = useState<ContentStatus>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editContent, setEditContent] = useState<Content | null>(null);
  const [showCategoryModal, setShowCategoryModal] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: [
      'content',
      typeFilter === 'all' ? undefined : typeFilter,
      statusFilter === 'all' ? undefined : statusFilter,
      page,
    ],
    queryFn: () =>
      contentApi.getAllContent(
        typeFilter === 'all' ? undefined : typeFilter,
        statusFilter === 'all' ? undefined : statusFilter,
        page,
        20,
      ),
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => contentApi.getCategories(),
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      type: string;
      title: string;
      body: string;
      categoryIds: string[];
      scheduledFor?: string;
    }) =>
      contentApi.createContent(
        data.type,
        data.title,
        data.body,
        data.categoryIds,
        data.scheduledFor,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      setShowCreateModal(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { title: string; body: string; categoryIds: string[]; scheduledFor?: string };
    }) => contentApi.updateContent(id, data.title, data.body, data.categoryIds, data.scheduledFor),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
      setEditContent(null);
    },
  });

  const publishMutation = useMutation({
    mutationFn: contentApi.publishContent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
    },
  });

  const unpublishMutation = useMutation({
    mutationFn: contentApi.unpublishContent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: contentApi.archiveContent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: contentApi.deleteContent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content'] });
    },
  });

  const handleDelete = (contentId: string, title: string) => {
    if (confirm(`Delete "${title}"? This cannot be undone.`)) {
      deleteMutation.mutate(contentId);
    }
  };

  const handleArchive = (contentId: string) => {
    if (confirm('Archive this content?')) {
      archiveMutation.mutate(contentId);
    }
  };

  const typeTabs: { value: ContentType; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'announcement', label: 'Announcements' },
    { value: 'document', label: 'Documents' },
    { value: 'newsletter', label: 'Newsletters' },
    { value: 'event', label: 'Events' },
  ];

  const statusTabs: { value: ContentStatus; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'draft', label: 'Drafts' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-slate/20 border-t-slate rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-[1100px]">
      {[publishMutation, unpublishMutation, archiveMutation, deleteMutation].some(
        (mutation) => mutation.isError,
      ) && (
        <p role="alert">
          {getErrorMessage(
            [publishMutation, unpublishMutation, archiveMutation, deleteMutation].find(
              (mutation) => mutation.isError,
            )?.error,
          )}
        </p>
      )}
      {/* Page Header */}
      <div className="mb-8">
        <span className="block text-[10px] tracking-[1.2px] text-[#68716d] mb-[9px] uppercase font-semibold">
          Opaskwayak Cree Nation
        </span>
        <h1 className="text-[32px] font-semibold text-[#424a47] leading-[1.2] tracking-[-0.5px] mb-[9px]">
          Content library
        </h1>
        <p className="text-[14px] text-[#68716d] leading-[1.6] mb-6">
          Keep your community informed, one update at a time.
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCategoryModal(true)}
            className="px-4 py-[10px] border border-[#e6e9de] rounded-[9px] hover:bg-[#f6f6f3] transition-colors text-[13px] font-medium text-[#424a47]"
          >
            Categories
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-[10px] bg-[#242728] text-white rounded-[9px] text-[13px] font-semibold hover:bg-[#3c413d] active:scale-[0.98] transition-all inline-flex items-center gap-2"
          >
            <svg
              className="w-[16px] h-[16px]"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.65}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Create content
          </button>
        </div>
      </div>

      {/* Tabs and Filters Card */}
      <div className="bg-white rounded-[13px] shadow-[0_2px_8px_rgba(0,0,0,0.06)] overflow-hidden mb-6">
        {/* Status Tabs */}
        <div className="flex gap-1 px-4 border-b border-[#e5e4df]">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setStatusFilter(tab.value);
                setPage(1);
              }}
              className={`px-4 py-[11px] font-medium text-[13px] transition-colors relative ${
                statusFilter === tab.value
                  ? 'text-[#424a47]'
                  : 'text-[#68716d] hover:text-[#424a47]'
              }`}
            >
              {tab.label}
              {statusFilter === tab.value && (
                <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4b607f]" />
              )}
            </button>
          ))}
        </div>

        {/* Type Filter */}
        <div className="px-4 py-4 border-b border-[#e5e4df]">
          <div className="flex items-center gap-3">
            <label className="text-[13px] font-medium text-[#68716d]">Filter by type:</label>
            <div className="flex gap-2">
              {typeTabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => {
                    setTypeFilter(tab.value);
                    setPage(1);
                  }}
                  className={`px-[11px] py-[5px] rounded-full text-[11px] font-medium border transition-colors ${
                    typeFilter === tab.value
                      ? 'bg-[#e1e5eb] text-[#4b607f] border-[#d1d8e3]'
                      : 'bg-[#f6f6f3] text-[#68716d] border-[#e5e4df] hover:bg-[#e8d8c9]'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content Table */}
        {data?.data.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-[64px] h-[64px] mx-auto mb-4 rounded-full bg-[#f6f6f3] flex items-center justify-center">
              <svg
                className="w-[32px] h-[32px] text-[#68716d]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.65}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-[18px] font-semibold text-[#424a47] mb-2">No content here yet</h3>
            <p className="text-[14px] text-[#68716d] mb-6">
              Create an update or try another filter
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-[11px] bg-[#242728] text-white rounded-[9px] text-[13px] font-semibold hover:bg-[#3c413d] active:scale-[0.98] transition-all inline-flex items-center gap-2"
            >
              <svg
                className="w-[16px] h-[16px]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.65}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Create content
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#f6f6f3] border-b border-[#e5e4df]">
                  <tr>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Title
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Updated
                    </th>
                    <th className="px-4 py-3 text-right text-[11px] font-medium text-[#68716d] uppercase tracking-[0.5px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e5e4df]">
                  {data?.data.map((content: Content) => (
                    <tr key={content.id} className="hover:bg-[#f6f6f3] transition-colors">
                      <td className="px-4 py-4">
                        <div className="font-medium text-[#424a47] text-[14px]">
                          {content.title}
                        </div>
                        <div className="text-[12px] text-[#68716d] truncate max-w-md mt-1">
                          {content.body.substring(0, 80)}…
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="px-[11px] py-[5px] rounded-full text-[11px] font-medium bg-[#f6f6f3] text-[#68716d] border border-[#e5e4df] capitalize">
                          {content.type}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-wrap gap-1">
                          {content.categories.map(({ category: cat }) => (
                            <span
                              key={cat.id}
                              className="px-[9px] py-[3px] text-[11px] rounded-full bg-[#e8d8c9] text-[#6f665e] border border-[#e5e2dc]"
                            >
                              {cat.name}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`px-[11px] py-[5px] rounded-full text-[11px] font-medium border capitalize ${
                            content.status === 'published'
                              ? 'bg-[#e1e5eb] text-[#4b607f] border-[#d1d8e3]'
                              : content.status === 'draft'
                                ? 'bg-[#fef8e6] text-[#b8942c] border-[#f4e6b8]'
                                : 'bg-[#f6f6f3] text-[#68716d] border-[#e5e4df]'
                          }`}
                        >
                          {content.status}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-[13px] text-[#68716d]">
                        {content.publishedAt
                          ? new Date(content.publishedAt).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : content.scheduledFor
                            ? `Scheduled: ${new Date(content.scheduledFor).toLocaleDateString()}`
                            : '-'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        {content.status !== 'published' && (
                          <button
                            onClick={() => publishMutation.mutate(content.id)}
                            disabled={publishMutation.isPending}
                            className="underline mr-3"
                          >
                            Publish
                          </button>
                        )}
                        {content.status === 'published' && (
                          <button
                            onClick={() => unpublishMutation.mutate(content.id)}
                            disabled={unpublishMutation.isPending}
                            className="underline mr-3"
                          >
                            Unpublish
                          </button>
                        )}
                        {content.status !== 'archived' && (
                          <button
                            onClick={() => handleArchive(content.id)}
                            className="underline mr-3"
                          >
                            Archive
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(content.id, content.title)}
                          className="text-red-700 mr-3"
                        >
                          Delete
                        </button>
                        <button
                          onClick={() => setEditContent(content)}
                          className="px-[9px] py-[7px] border-0 bg-transparent hover:bg-[#f6f6f3] rounded-[7px] transition-colors"
                          aria-label={`Manage ${content.title}`}
                        >
                          <svg
                            className="w-[18px] h-[18px] text-[#68716d]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={1.65}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Table Footer */}
            <div className="px-4 py-4 border-t border-[#e5e4df] flex items-center justify-between">
              <span className="text-[13px] text-[#68716d]">
                {data?.data.length || 0} content {data?.data.length === 1 ? 'item' : 'items'}
              </span>
              {data?.pagination && data.pagination.totalPages > 1 && (
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-[9px] border border-[#e6e9de] rounded-[9px] hover:bg-[#f6f6f3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-[13px] font-medium text-[#424a47]"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-[9px] text-[13px] font-medium text-[#424a47] min-w-[44px] text-center">
                    {page}
                  </span>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pagination.totalPages}
                    className="px-4 py-[9px] border border-[#e6e9de] rounded-[9px] hover:bg-[#f6f6f3] transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-[13px] font-medium text-[#424a47]"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create/Edit Content Modal */}
      {(showCreateModal || editContent) && (
        <ContentFormModal
          content={editContent}
          categories={categories || []}
          onClose={() => {
            setShowCreateModal(false);
            setEditContent(null);
          }}
          onSubmit={(data) => {
            if (editContent) {
              updateMutation.mutate({ id: editContent.id, data });
            } else {
              createMutation.mutate(data);
            }
          }}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Category Manager Modal */}
      {showCategoryModal && (
        <CategoryManagerModal
          categories={categories || []}
          onClose={() => setShowCategoryModal(false)}
        />
      )}
    </div>
  );
}

// Content Form Modal Component
function ContentFormModal({
  content,
  categories,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  content: Content | null;
  categories: Category[];
  onClose: () => void;
  onSubmit: (data: any) => void;
  isSubmitting: boolean;
}) {
  const [contentType, setContentType] = useState(content?.type || 'announcement');
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    content?.categories.map((c) => c.category.id) || [],
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    onSubmit({
      type: content?.type || String(formData.get('type')),
      title: formData.get('title') as string,
      body: formData.get('body') as string,
      categoryIds: selectedCategories,
      scheduledFor: formData.get('scheduledFor')
        ? new Date(String(formData.get('scheduledFor'))).toISOString()
        : undefined,
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-card shadow-card p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-charcoal mb-1">
              {content ? 'Edit Content' : 'Create Content'}
            </h2>
            <p className="text-sm text-muted">
              {content
                ? 'Update content details and publish settings'
                : 'Create a new announcement or document'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted hover:text-charcoal text-2xl font-light leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-charcoal mb-2">
              Type
            </label>
            <select
              id="type"
              name="type"
              value={contentType}
              disabled={!!content}
              onChange={(event) => {
                setContentType(event.target.value);
                setSelectedCategories([]);
              }}
              required
              className="w-full px-4 py-2.5 text-base border border-line rounded-input bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-slate focus:border-transparent transition-all"
            >
              <option value="announcement">Announcement</option>
              <option value="document">Document</option>
              <option value="newsletter">Newsletter</option>
              <option value="event">Event</option>
            </select>
          </div>

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-charcoal mb-2">
              Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              defaultValue={content?.title || ''}
              required
              className="w-full px-4 py-2.5 text-base border border-line rounded-input bg-white text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-slate focus:border-transparent transition-all"
              placeholder="Enter content title"
            />
          </div>

          <div>
            <label htmlFor="body" className="block text-sm font-medium text-charcoal mb-2">
              Body
            </label>
            <textarea
              id="body"
              name="body"
              defaultValue={content?.body || ''}
              required
              rows={10}
              className="w-full px-4 py-2.5 text-base border border-line rounded-input bg-white text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-slate focus:border-transparent transition-all resize-y"
              placeholder="Enter content body"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-charcoal mb-3">Categories</label>
            <div className="space-y-2 bg-workspace rounded-input p-4 border border-line">
              {categories
                .filter((cat) => cat.contentType === contentType)
                .map((cat) => (
                  <label key={cat.id} className="flex items-center cursor-pointer group">
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(cat.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCategories([...selectedCategories, cat.id]);
                        } else {
                          setSelectedCategories(selectedCategories.filter((id) => id !== cat.id));
                        }
                      }}
                      className="h-5 w-5 text-slate border-line rounded accent-slate focus:ring-2 focus:ring-slate"
                    />
                    <span className="ml-3 text-sm text-charcoal group-hover:text-slate transition-colors">
                      {cat.name}
                    </span>
                  </label>
                ))}
            </div>
          </div>

          <div>
            <label htmlFor="scheduledFor" className="block text-sm font-medium text-charcoal mb-2">
              Schedule For (Optional)
            </label>
            <input
              type="datetime-local"
              id="scheduledFor"
              name="scheduledFor"
              defaultValue={content?.scheduledFor?.slice(0, 16) || ''}
              className="w-full px-4 py-2.5 text-base border border-line rounded-input bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-slate focus:border-transparent transition-all"
            />
            <p className="text-xs text-muted mt-1.5">
              Leave empty to save as a draft; publish it from the content list.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-charcoal text-white py-3 rounded-button text-sm font-semibold hover:bg-charcoal/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-button"
            >
              {isSubmitting ? 'Saving...' : content ? 'Update Content' : 'Create Content'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-line rounded-button hover:bg-workspace transition-colors text-sm font-medium text-charcoal"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Category Manager Modal Component
function CategoryManagerModal({
  categories,
  onClose,
}: {
  categories: Category[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);

  const createCategoryMutation = useMutation({
    mutationFn: (data: { slug: string; name: string; contentType: string }) =>
      contentApi.createCategory(data.slug, data.name, data.contentType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setShowCreateForm(false);
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: contentApi.deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    createCategoryMutation.mutate({
      slug: formData.get('slug') as string,
      name: formData.get('name') as string,
      contentType: formData.get('contentType') as string,
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-card shadow-card p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-charcoal mb-1">Manage Categories</h2>
            <p className="text-sm text-muted">Create and manage content categories</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted hover:text-charcoal text-2xl font-light leading-none"
          >
            ×
          </button>
        </div>

        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="mb-6 px-4 py-2.5 bg-charcoal text-white rounded-button text-sm font-semibold hover:bg-charcoal/90 active:scale-[0.98] transition-all shadow-button"
        >
          {showCreateForm ? 'Cancel' : '+ New Category'}
        </button>

        {showCreateForm && (
          <form
            onSubmit={handleCreate}
            className="mb-6 p-5 bg-workspace border border-line rounded-card space-y-4"
          >
            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-charcoal mb-2">
                Slug
              </label>
              <input
                type="text"
                id="slug"
                name="slug"
                required
                placeholder="general-updates"
                className="w-full px-4 py-2.5 text-base border border-line rounded-input bg-white text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-slate focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-charcoal mb-2">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                placeholder="General Updates"
                className="w-full px-4 py-2.5 text-base border border-line rounded-input bg-white text-charcoal placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-slate focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label htmlFor="contentType" className="block text-sm font-medium text-charcoal mb-2">
                Content Type
              </label>
              <select
                id="contentType"
                name="contentType"
                required
                className="w-full px-4 py-2.5 text-base border border-line rounded-input bg-white text-charcoal focus:outline-none focus:ring-2 focus:ring-slate focus:border-transparent transition-all"
              >
                <option value="announcement">Announcements Only</option>
                <option value="document">Documents Only</option>
                <option value="newsletter">Newsletters Only</option>
                <option value="event">Events Only</option>
              </select>
            </div>
            {createCategoryMutation.isError && (
              <p role="alert">{getErrorMessage(createCategoryMutation.error)}</p>
            )}
            <button
              type="submit"
              disabled={createCategoryMutation.isPending}
              className="w-full px-4 py-3 bg-slate text-white rounded-button text-sm font-semibold hover:bg-slate/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-button"
            >
              {createCategoryMutation.isPending ? 'Creating...' : 'Create Category'}
            </button>
          </form>
        )}

        <div className="space-y-2">
          {categories.length === 0 ? (
            <div className="text-center py-8 text-muted">
              <p className="text-sm">No categories yet</p>
            </div>
          ) : (
            categories.map((cat) => (
              <div
                key={cat.id}
                className="flex justify-between items-center p-4 border border-line rounded-button hover:bg-workspace transition-colors"
              >
                <div>
                  <div className="font-medium text-charcoal">{cat.name}</div>
                  <div className="text-sm text-muted mt-0.5">
                    {cat.slug} • {cat.contentType}
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (confirm(`Delete category "${cat.name}"?`)) {
                      deleteCategoryMutation.mutate(cat.id);
                    }
                  }}
                  disabled={deleteCategoryMutation.isPending}
                  className="text-clay hover:underline text-sm font-medium disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default ContentPage;
